import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  HostListener,
  ElementRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { InactivityService } from '../auth/inactivity.service';
import { AuthService } from '../auth/auth.service';
import { ConstantsService } from '../constants.service';
import { MatDialog } from '@angular/material/dialog';
import { CreateTicketModalComponent } from '../features/tickets/components/create-ticket-modal/create-ticket-modal.component';
import { DialogData, GenericConfirmationDialogComponent } from '../generic-confirmation-dialog/generic-confirmation-dialog.component';
import { PolicyService } from '../policy.service';
import { SessionService } from '../session.service';
import { HttpClient } from '@angular/common/http';
import { WebsocketService } from '../shared/services/websocket/websocket.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { animate, style, transition, trigger } from '@angular/animations';
import { catchError, forkJoin, map, of } from 'rxjs';

export interface Notification {
  id: string;
  clientId: string;
  userId: string;
  sourceEventId: string;
  type: string;
  title: string;
  summary: string;
  severity: 'INFO' | 'WARN' | 'SUCCESS' | 'CRITICAL';
  meta: Record<string, any>;
  createdAt: string;
  channelInApp: boolean;
  channelEmail: boolean;
  channelSms: boolean;
  readAt: string | null;
}

@Component({
  selector: 'app-complydome',
  templateUrl: './complydome.component.html',
  styleUrls: ['./complydome.component.scss'],
  animations: [
    trigger('notificationItem', [
      transition(':enter', [
        style({ transform: 'translateX(110%)', opacity: 0 }),
        animate(
          '300ms cubic-bezier(0.22, 0.61, 0.36, 1)',
          style({ transform: 'translateX(0)', opacity: 1 })
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({
            transform: 'translateX(110%)',
            opacity: 0,
            height: '0px',
            marginTop: '0px',
            marginBottom: '0px',
            paddingTop: '0px',
            paddingBottom: '0px',
            borderWidth: '0px',
          })
        ),
      ]),
    ]),
  ],
})
export class ComplydomeComponent implements OnInit, OnDestroy, AfterViewInit {
  // === Current year ===
  currentYear = new Date().getFullYear();

  // === Policy flags ===
  policyRolePermission = '';
  policyViewUsers = '';
  policyViewRole = '';
  policyViewEmployees = '';
  policyViewVendors = '';
  policyScheduleTrainingForVendors = '';
  policyScheduleTrainingForEmployees = '';
  policyViewSmGlobal = '';
  policyViewUmGlobal = '';
  // dashboard
  policyViewDshGlobal = '';
  policyViewDshGeneralView = '';
  policyViewDshVendor = '';
  policyViewDshEmployee = '';
  policyViewDshPolicies = '';
  policyViewDshUserAudits = '';
  policyViewDshIncidentsNotifications = '';
  policyViewDshIncidents = '';
  policyViewDshAssets = '';
  // policy management
  policyViewPmGlobal = '';
  policyViewPmViewPolicies = '';
  policyViewRiskAssessmentGlobal = '';
  policyViewComplianceGlobal = '';
  policyViewMpfGlobal = '';
  policyViewMpfViewLicences = '';
  policyViewMpfAddLicence = '';
  policyViewMpfEditLicence = '';
  policyViewMpfDeleteLicence = '';
  policyViewMpfViewAgreements = '';
  policyViewMpfAddAgreement = '';
  policyViewMpfEditAgreement = '';
  policyViewMpfDeleteAgreement = '';
  policyViewMpfViewTrainings = '';
  policyViewMpfAddTraining = '';
  policyViewMpfEditTraining = '';
  policyViewMpfDeleteTraining = '';
  policyViewMpfViewCertitifications = '';
  policyViewMpfAddCertification = '';
  policyViewMpfEditCertification = '';
  policyViewMpfDeleteCertification = '';
  policyViewMpfViewComplianceChecks = '';
  policyViewMpfViewAssets = '';
  policyViewSubscription = '';
  policyViewPayment = '';
  policyViewPaymentMethod = '';
  policyViewPolicies = '';
  policyViewAcknowledgment = '';
  policyViewOigGlobal = '';
  policyViewOigViewEmployee = '';
  policyViewOigViewVendor = '';
  policyViewRiskAssessment = '';
  policyViewIncidents = '';
  policyViewRaGlobal = '';
  policyViewRaViewRiskAssessment = '';
  // reports
  policyReportsGlobal = '';
  policyReportsViewReports = '';
  // audit management
  policyViewAuditManagementGlobal = '';
  policyViewAuditManagementViewAuditPreparation = '';

  public pathname = location.pathname;

  // === Sidebar/Notification/Options states ===
  isSidebarVisible = false;
  isNotificationBarVisible = false;
  isOptionsBarVisible = true;
  isSidebarCollapsed = false;
  isSidebarCollapsing = false;
  isNotificationCollapsed = true;

  // === Notifications ===
  notifications: Notification[] = [];
  private readonly pendingNotificationRemovals = new Set<string>();
  isClearAllConfirmVisible = false;
  isClearAllPending = false;

  private readonly currentUserId: string;
  private readonly clientId: string;
  private apiUrl = '';
  private sidebarCollapseTimeoutId: ReturnType<typeof setTimeout> | null = null;
  popup: 'privacy' | 'service' | 'accessibility' | null = null;
  isMobile = false;

  constructor(
    public router: Router,
    private inactivityService: InactivityService,
    private authService: AuthService,
    private constantService: ConstantsService,
    private dialog: MatDialog,
    private sessionService: SessionService,
    private httpClient: HttpClient,
    private socket: WebsocketService,
    private breakpointObserver: BreakpointObserver,
    private elRef: ElementRef
  ) {
    this.clientId = sessionService.getClientId();
    this.currentUserId = this.sessionService.getValue('user_id');
    this.breakpointObserver.observe(['(max-width: 64em)']).subscribe((r) => {
      this.isMobile = r.matches;
    });
  }

  private updateNotificationCollapseState() {
    const hasUnread = this.unreadNotificationCount > 0;
    this.isNotificationCollapsed = !hasUnread;
    if (!hasUnread) {
      this.isNotificationBarVisible = false;
      this.isClearAllConfirmVisible = false;
    }
  }

  private parseNotificationTimestamp(createdAt: string): number {
    const timestamp = new Date(createdAt).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private sortNotificationsByNewestFirst(
    notifications: Notification[]
  ): Notification[] {
    return [...notifications].sort(
      (a, b) =>
        this.parseNotificationTimestamp(b.createdAt) -
        this.parseNotificationTimestamp(a.createdAt)
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const sidebar = document.querySelector('.navbar');
    const sidebarBurger = document.querySelector('.burger-menu:not(.right)');
    const notificationBar = document.querySelector('.notification-bar');
    const notificationBurger = document.querySelector('.burger-menu.right');
    const floatingNotificationBurger = document.querySelector(
      '.floating-notification-burger'
    );

    const target = event.target as Node;

    if (
      this.isSidebarVisible &&
      sidebar &&
      !sidebar.contains(target) &&
      sidebarBurger &&
      !sidebarBurger.contains(target)
    ) {
      this.isSidebarVisible = false;
    }

    if (
      this.isNotificationBarVisible &&
      notificationBar &&
      !notificationBar.contains(target) &&
      (!notificationBurger || !notificationBurger.contains(target)) &&
      (!floatingNotificationBurger ||
        !floatingNotificationBurger.contains(target))
    ) {
      this.isNotificationBarVisible = false;
      this.isClearAllConfirmVisible = false;
    }
  }

  ngOnInit() {
    this.inactivityService.startWatching();

    this.socket.connect();

    this.socket.subscribe('*', (payload: Notification) => {
      const notificationType = (payload as any).notificationType;
      if (payload.type === 'PING' || notificationType === 'AI_INSIGHT' || notificationType === 'ALERT') {
        return;
      }

      // @ts-ignore
      payload.id = payload.id;
      this.notifications = this.sortNotificationsByNewestFirst([
        ...this.notifications,
        payload,
      ]);
      this.updateNotificationCollapseState();
    });

    this.constantService.onReady(() => {
      // dashboard
      this.policyViewDshGlobal = PolicyService.LOGGED_IN_USER_POLICY_ID;
      this.policyViewDshGeneralView = PolicyService.LOGGED_IN_USER_POLICY_ID;
      this.policyViewDshVendor = this.constantService.getConstant('dsh-vendor-view');
      this.policyViewDshEmployee = this.constantService.getConstant('dsh-employee-view');
      this.policyViewDshPolicies = this.constantService.getConstant('dsh-policies-view');
      this.policyViewDshUserAudits = this.constantService.getConstant('dsh-user-audits-view');
      this.policyViewDshIncidentsNotifications = this.constantService.getConstant('dsh-incidents-notifications-view');
      this.policyViewDshIncidents = this.constantService.getConstant('dsh-incidents-view');
      this.policyViewDshAssets = this.constantService.getConstant('dsh-assets-view');

      // policy management
      this.policyViewPmGlobal = this.constantService.getConstant('pm-global');
      this.policyViewPmViewPolicies = this.constantService.getConstant('pm-view-policies');

      // my portfolio
      this.policyViewMpfGlobal = PolicyService.LOGGED_IN_USER_POLICY_ID;
      this.policyViewMpfViewLicences = PolicyService.LOGGED_IN_USER_POLICY_ID;
      this.policyViewMpfViewAgreements = PolicyService.LOGGED_IN_USER_POLICY_ID;
      this.policyViewMpfViewTrainings = PolicyService.LOGGED_IN_USER_POLICY_ID;
      this.policyViewMpfViewCertitifications = PolicyService.LOGGED_IN_USER_POLICY_ID;
      this.policyViewMpfViewComplianceChecks = PolicyService.LOGGED_IN_USER_POLICY_ID;
      this.policyViewMpfViewAssets = PolicyService.LOGGED_IN_USER_POLICY_ID;

      // risk assessment
      this.policyViewRaGlobal = this.constantService.getConstant('ra-global');
      this.policyViewRaViewRiskAssessment = this.constantService.getConstant('ra-view-risk-assessment');

      // incident management
      this.policyViewIncidents = this.constantService.getConstant('ct-im-view-incidents');

      // reports
      this.policyReportsGlobal = this.constantService.getConstant('r-global');
      this.policyReportsViewReports = this.constantService.getConstant('r-view-reports');

      // audit management
      this.policyViewAuditManagementGlobal = this.constantService.getConstant('am-global');
      this.policyViewAuditManagementViewAuditPreparation = this.constantService.getConstant('am-view-audit-preparation');

      this.policyViewUmGlobal = this.constantService.getConstant('um-global');
      this.policyViewPmGlobal = this.constantService.getConstant('pm-global');
      this.policyViewRiskAssessmentGlobal = this.constantService.getConstant('ra-global');
      this.policyViewComplianceGlobal = this.constantService.getConstant('cm-global');
      this.policyViewEmployees = this.constantService.getConstant('em-global');
      this.policyViewVendors = this.constantService.getConstant('vm-global');
      this.policyViewOigGlobal = this.constantService.getConstant('oig-global');
      this.policyViewOigViewEmployee = this.constantService.getConstant('oig-view-employee');
      this.policyViewOigViewVendor = this.constantService.getConstant('oig-view-vendor');
      this.policyViewRiskAssessment = this.constantService.getConstant('ra-view-risk-assessment');
      this.policyScheduleTrainingForEmployees =
        this.constantService.getConstant('em-execute-scheduled-trainings');
      this.policyScheduleTrainingForVendors = this.constantService.getConstant(
        'vm-execute-scheduled-trainings'
      );
      this.policyRolePermission = this.constantService.getConstant(
        'um-assign-role-permissions'
      );
      this.policyViewUsers = this.constantService.getConstant('um-view-user');
      this.policyViewRole = this.constantService.getConstant('um-view-roles');
      this.policyViewSmGlobal = this.constantService.getConstant('sm-global');
      this.policyViewSubscription = this.constantService.getConstant(
        'sm-view-subscription'
      );
      this.policyViewPayment =
        this.constantService.getConstant('sm-view-payment');
      this.policyViewPaymentMethod = this.constantService.getConstant(
        'sm-wiew-payment-method'
      );
      this.policyViewPolicies = PolicyService.LOGGED_IN_USER_POLICY_ID;
      this.policyViewAcknowledgment = PolicyService.LOGGED_IN_USER_POLICY_ID;
    });

    this.router.events.subscribe(() => {
      this.pathname = location.pathname;
    });
  }

  ngAfterViewInit(): void {
    this.updateNotificationCollapseState();
    this.constantService.onReady(() => {
      this.apiUrl = this.constantService.getConstant('apiUrl');

      this.getNotifications().subscribe({
        next: (data) => {
          this.notifications = this.sortNotificationsByNewestFirst(data ?? []);
          this.updateNotificationCollapseState();
        },
        error: (err) => {
          console.error('Error fetching notifications', err);
        },
      });
    });
  }

  ngOnDestroy() {
    this.inactivityService.stopWatching();
    if (this.sidebarCollapseTimeoutId) {
      clearTimeout(this.sidebarCollapseTimeoutId);
      this.sidebarCollapseTimeoutId = null;
    }
  }

  // === API call for notifications ===
  private getNotifications() {
    const endpoint =
      this.apiUrl +
      `/central-audit/api/notifications?clientId=${this.clientId}&userId=${this.currentUserId}&unreadOnly=true`;

    return this.httpClient.get<Notification[]>(endpoint, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
  }

  private markNotificationAsRead(id: string) {
    const endpoint = `${this.apiUrl}/central-audit/api/notifications/${id}/read`;
    return this.httpClient.patch(
      endpoint,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      }
    );
  }

  toggleSidebarCollapse() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    this.isSidebarCollapsing = true;

    if (this.sidebarCollapseTimeoutId) {
      clearTimeout(this.sidebarCollapseTimeoutId);
    }

    this.sidebarCollapseTimeoutId = setTimeout(() => {
      this.isSidebarCollapsing = false;
      this.sidebarCollapseTimeoutId = null;
    }, 320);
  }

  toggleSidebar(): void {
    this.isSidebarVisible = !this.isSidebarVisible;
    if (this.isSidebarVisible) {
      this.isNotificationBarVisible = false;
      this.isOptionsBarVisible = false;
    }
  }

  onNavMenuClick(event: MouseEvent): void {
    if (!this.isMobile || !this.isSidebarVisible) {
      return;
    }

    const target = event.target as HTMLElement | null;
    const clickedNavLink = target?.closest('a.nav-link');
    if (clickedNavLink) {
      this.isSidebarVisible = false;
    }
  }

  toggleNotificationBar(): void {
    this.toggleNotificationPanel();
  }

  toggleNotificationPanel(event?: MouseEvent): void {
    event?.stopPropagation();
    this.isNotificationBarVisible = !this.isNotificationBarVisible;
    if (this.isNotificationBarVisible) {
      this.isSidebarVisible = false;
      this.isOptionsBarVisible = false;
    } else {
      this.isClearAllConfirmVisible = false;
    }
  }

  toggleClearAllConfirmation(event?: MouseEvent): void {
    event?.stopPropagation();

    if (this.isClearAllPending || this.unreadNotificationCount === 0) {
      return;
    }

    this.isClearAllConfirmVisible = !this.isClearAllConfirmVisible;
  }

  cancelClearAllConfirmation(event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.isClearAllPending) {
      return;
    }

    this.isClearAllConfirmVisible = false;
  }

  confirmClearAllNotifications(event?: MouseEvent): void {
    event?.stopPropagation();

    if (this.isClearAllPending) {
      return;
    }

    const unreadNotifications = this.notifications.filter((n) => !n.readAt);
    if (unreadNotifications.length === 0) {
      this.isClearAllConfirmVisible = false;
      return;
    }

    const wasNotificationBarVisible = this.isNotificationBarVisible;
    this.isClearAllPending = true;
    this.isClearAllConfirmVisible = false;
    unreadNotifications.forEach((notification) =>
      this.pendingNotificationRemovals.add(notification.id)
    );

    this.notifications = this.notifications.filter((n) => n.readAt);
    this.updateNotificationCollapseState();

    const markReadRequests = unreadNotifications.map((notification) =>
      this.markNotificationAsRead(notification.id).pipe(
        map(() => ({ id: notification.id, ok: true as const })),
        catchError((error) =>
          of({ id: notification.id, ok: false as const, error })
        )
      )
    );

    forkJoin(markReadRequests).subscribe((results) => {
      const failedResults = results.filter((result) => !result.ok);
      const failedIds = new Set(failedResults.map((result) => result.id));

      unreadNotifications.forEach((notification) =>
        this.pendingNotificationRemovals.delete(notification.id)
      );
      this.isClearAllPending = false;

      if (failedIds.size > 0) {
        const notificationsToRestore = unreadNotifications.filter((n) =>
          failedIds.has(n.id)
        );
        this.notifications = this.sortNotificationsByNewestFirst([
          ...this.notifications,
          ...notificationsToRestore,
        ]);
        this.updateNotificationCollapseState();
        if (wasNotificationBarVisible) {
          this.isNotificationBarVisible = true;
        }
        console.error(
          'Failed to mark some notifications as read:',
          failedResults
        );
      }
    });
  }

  toggleOptionsBar(): void {
    this.isOptionsBarVisible = !this.isOptionsBarVisible;
    if (this.isOptionsBarVisible) {
      this.isSidebarVisible = false;
      this.isNotificationBarVisible = false;
    }
  }

  showSidebar(): void {
    this.isSidebarVisible = true;
    this.isNotificationBarVisible = false;
    this.isOptionsBarVisible = false;
  }

  showNotificationBar(): void {
    this.isNotificationBarVisible = true;
    this.isSidebarVisible = false;
    this.isOptionsBarVisible = false;
  }

  showOptionsBar(): void {
    this.isOptionsBarVisible = true;
    this.isSidebarVisible = false;
    this.isNotificationBarVisible = false;
  }

  removeNotification(id: string, event?: MouseEvent): void {
    event?.stopPropagation();
    this.isClearAllConfirmVisible = false;

    if (this.isClearAllPending || this.pendingNotificationRemovals.has(id)) {
      return;
    }

    const notificationIndex = this.notifications.findIndex((n) => n.id === id);
    if (notificationIndex === -1) {
      return;
    }

    const notificationToRestore = this.notifications[notificationIndex];
    const wasNotificationBarVisible = this.isNotificationBarVisible;
    this.pendingNotificationRemovals.add(id);
    this.notifications = this.notifications.filter((n) => n.id !== id);
    this.updateNotificationCollapseState();

    this.markNotificationAsRead(id).subscribe({
      next: () => {
        this.pendingNotificationRemovals.delete(id);
      },
      error: (err) => {
        this.pendingNotificationRemovals.delete(id);
        const alreadyRestored = this.notifications.some((n) => n.id === id);
        if (!alreadyRestored) {
          const restoredNotifications = [...this.notifications];
          const restoreIndex = Math.min(
            notificationIndex,
            restoredNotifications.length
          );
          restoredNotifications.splice(
            restoreIndex,
            0,
            notificationToRestore
          );
          this.notifications =
            this.sortNotificationsByNewestFirst(restoredNotifications);
          this.updateNotificationCollapseState();
          if (wasNotificationBarVisible) {
            this.isNotificationBarVisible = true;
          }
        }
        console.error('Failed to mark notification as read:', err);
      },
    });
  }

  isNotificationRemovalPending(id: string): boolean {
    return this.pendingNotificationRemovals.has(id);
  }

  // === Session ===
  logout(): void {
    const dialogData: DialogData = {
      title: 'Sign Out of Complydome?',
      message: 'You will be securely logged out of your active session. If you have Complydome open in any other browser windows or tabs, those sessions will also be terminated to protect your data.',
      confirmButtonText: 'Sign Out Everywhere',
      cancelButtonText: 'Cancel',
      icon: 'logout',
      iconClass: 'icon-danger',
    };

    const dialogRef = this.dialog.open(GenericConfirmationDialogComponent, {
      data: dialogData,
      panelClass: 'confirm-dialog-panel',
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.signOut();
    });
  }

  private signOut(): void {
    this.authService.logout().subscribe({
      next: (response) => {
        console.log('Logged out successfully:', response);
        this.router.navigate(['/login']);
      },
      error: (error) => {
        console.error('Logout failed:', error);
      },
    });
  }

  displayCreateNewTicketModal() {
    this.dialog.open(CreateTicketModalComponent, {
      width: '480px',
      panelClass: 'confirm-dialog-panel',
      autoFocus: false,
      restoreFocus: false,
      disableClose: true,
    });
  }

  openPopup(type: 'privacy' | 'service' | 'accessibility') {
    this.popup = type;
  }

  closePopup() {
    this.popup = null;
  }

  get hasUnread(): boolean {
    return this.notifications.some((n) => !n.readAt);
  }

  get unreadNotificationCount(): number {
    return this.notifications.filter((n) => !n.readAt).length;
  }

  getNotificationSeverityClass(severity: Notification['severity']): string {
    switch (severity) {
      case 'SUCCESS':
        return 'success';
      case 'WARN':
        return 'warn';
      case 'CRITICAL':
        return 'critical';
      default:
        return 'info';
    }
  }
}
